import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProgressbarPageStore, PayflowProgressBarManager } from '@ctct/application-layer';
import { BillingStrategy, type BillingStrategyQuery } from '@ctct/domain-layer';
import type { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import type { PayflowProgressBarHooks } from '@ctct/state-client-layer';
import { useAddOnsFacade, usePlanGroupFacade } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';

interface Props {
  initializer: Pick<PayflowProgressBarManager, 'payflowProgressBarInit'>;
  pageStore: ProgressbarPageStore;
  hooks: PayflowProgressBarHooks;
  flags: Parameters<PayflowProgressBarManager['payflowProgressBarInit']>[0]['flags'];
  billingStrategy: BillingStrategyQuery;
  flowId: AccountModificationFlowId;
}

const resolveBillingStrategyEnum = (billingStrategy: BillingStrategyQuery): BillingStrategy =>
  billingStrategy.isPending ? BillingStrategy.InHouse : billingStrategy.data;

export const usePayflowProgressBarWidget = ({
  initializer,
  pageStore,
  hooks,
  flags,
  billingStrategy,
  flowId,
}: Props) => {
  const { t } = useTranslation();

  const {
    isSecureCheckout,
    isCustomerFlow,
    isCanceled,
    isSuspended,
    isMWebVariantEnabled,
    isValueFocusedMWebPlanSelectionEnabled,
    hideAddons,
    isSmsBundle,
  } = flags;

  const planGroupQuery = usePlanGroupFacade();
  const resolvedBillingStrategy = resolveBillingStrategyEnum(billingStrategy);

  const planId = isCanceled ? planGroupQuery.data?.getRecommendedPlanId() : planGroupQuery.data?.getCurrentPlanId();

  const addOnsQuery = useAddOnsFacade(planId);

  const { lifecycle, ...componentProps } = useMemo(() => {
    const returnValue = initializer.payflowProgressBarInit({
      pageStore,
      queries: [planGroupQuery, addOnsQuery],
      flags: {
        isSecureCheckout,
        isCustomerFlow,
        isCanceled,
        isSuspended,
        isMWebVariantEnabled,
        isValueFocusedMWebPlanSelectionEnabled,
        hideAddons,
        isSmsBundle,
      },
      t,
      billingStrategy: resolvedBillingStrategy,
      flowId,
    });

    return {
      ...returnValue,
      hooks,
    };
  }, [
    initializer,
    pageStore,
    planGroupQuery,
    addOnsQuery,
    isSecureCheckout,
    isCustomerFlow,
    isCanceled,
    isSuspended,
    isMWebVariantEnabled,
    isValueFocusedMWebPlanSelectionEnabled,
    hideAddons,
    isSmsBundle,
    t,
    resolvedBillingStrategy,
    flowId,
    hooks,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle);

  return componentProps;
};

export type PayflowProgressBarWidgetProps = ReturnType<typeof usePayflowProgressBarWidget>;
